/**
 * WORKER DEPO — Cloudflare Worker + D1 üzerinden konuşan istemci.
 *
 * Bu sınıf `fetch` kullanır; oturum `httpOnly` çerezde taşınır ve JavaScript
 * erişemez (XSS ile çalınsa bile oturum kurtarılamaz).
 *
 * AĞ İYİĞİ: `credentials: 'include'` şart. Aksi halde tarayıcı çerezi
 * göndermez ve her istek "Oturum gerekli" alır.
 */

import { type ContentPack, validateContentPack } from '@tttrpg/rules'
import type { AuthState, CharacterRow, InviteSummary, Repository, RollLogEntry } from './contract'

export interface WorkerRepositoryOptions {
  /** Worker temel adresi, sondaki eğik çizgi önemli değil. */
  baseUrl: string
  /** Zaman aşımı (ms). Varsayılan 15 sn. */
  timeoutMs?: number
  /** Geliştirme sırasında istekleri konsola yaz. */
  debug?: boolean
}

/** Worker'ın döndüğü hata gövdesi. */
interface ErrorBody {
  error?: string
  details?: string[]
}

export class WorkerError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'WorkerError'
    this.status = status
  }
}

const DEFAULT_TIMEOUT = 15_000

export function createWorkerRepository(options: WorkerRepositoryOptions): Repository {
  const base = options.baseUrl.replace(/\/+$/, '')
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT

  /**
   * Ortak istek yardımcısı.
   *
   * Zaman aşımı şart: ağ takılırsa `fetch` sonsuza kadar bekleyebilir ve
   * arayüz sonsuza kadar "yükleniyor" görünür.
   */
  async function request<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    const headers = new Headers(init.headers)
    if (init.json !== undefined) headers.set('Content-Type', 'application/json')

    if (options.debug) console.debug(`[zarhane] ${init.method ?? 'GET'} ${base}${path}`)

    let response: Response
    try {
      response = await fetch(`${base}${path}`, {
        ...init,
        headers,
        credentials: 'include',
        signal: controller.signal,
        ...(init.json !== undefined ? { body: JSON.stringify(init.json) } : {}),
      })
    } catch (error) {
      if (controller.signal.aborted) {
        throw new WorkerError('Sunucu yanıt vermedi. Bağlantını kontrol et.', 0)
      }
      // Ağ hatası genellikle "Worker adresi yanlış / Worker uyku modunda".
      throw new WorkerError(
        `Sunucuya ulaşılamadı. (${error instanceof Error ? error.message : 'bilinmeyen hata'})`,
        0,
      )
    } finally {
      clearTimeout(timer)
    }

    // 204/205 gövdesi yok.
    if (response.status === 204) return undefined as T

    const text = await response.text()
    let body: unknown = null
    if (text) {
      try {
        body = JSON.parse(text)
      } catch {
        body = null
      }
    }

    if (!response.ok) {
      const message = (body as ErrorBody | null)?.error
      throw new WorkerError(message ?? `Sunucu ${response.status} döndü.`, response.status)
    }

    return body as T
  }

  const repo: Repository = {
    backend: 'worker',

    // --- Kimlik ---------------------------------------------------------
    async getAuth(): Promise<AuthState> {
      const body = await request<{ user: PublicUser | null }>('/api/auth/me')

      if (!body?.user) {
        return {
          status: 'signed-out',
          userId: null,
          email: null,
          displayName: null,
          role: null,
          viaInvite: false,
          backend: 'worker',
        }
      }

      return {
        status: 'signed-in',
        userId: body.user.id,
        email: body.user.email,
        displayName: body.user.displayName,
        role: body.user.role,
        // Sunucu bu bilgiyi ayrı tutmaz; yerel işaret olarak saklanır.
        viaInvite: false,
        backend: 'worker',
      }
    },

    async signIn(email, password) {
      return guard(async () => {
        await request('/api/auth/sign-in', { method: 'POST', json: { email, password } })
        return { ok: true as const }
      })
    },

    async signUp(email, password, signUpOptions) {
      return guard(async () => {
        await request('/api/auth/sign-up', {
          method: 'POST',
          json: {
            email,
            password,
            displayName: signUpOptions?.displayName,
            inviteCode: signUpOptions?.inviteCode,
          },
        })
        return { ok: true as const }
      })
    },

    async signOut() {
      // Çerezi temizlemek sunucuya bağlı; ağ yoksa da yerel durumu sıfırla.
      await request('/api/auth/sign-out', { method: 'POST' }).catch(() => undefined)
    },

    async changePassword(currentPassword, newPassword) {
      return guard(async () => {
        await request('/api/auth/change-password', {
          method: 'POST',
          json: { currentPassword, newPassword },
        })
        return { ok: true as const }
      })
    },

    async setDisplayName(displayName) {
      return guard(async () => {
        await request('/api/auth/display-name', {
          method: 'PATCH',
          json: { displayName },
        })
        return { ok: true as const }
      })
    },

    // --- Karakterler ----------------------------------------------------
    async listCharacters() {
      const body = await request<{ characters: CharacterRow[] }>('/api/characters')
      return body.characters ?? []
    },

    async getCharacter(id) {
      const body = await request<{ character: CharacterRow | null }>(
        `/api/characters/${encodeURIComponent(id)}`,
      )
      return body.character ?? null
    },

    async createCharacter(row) {
      const body = await request<{ character: CharacterRow }>('/api/characters', {
        method: 'POST',
        json: {
          name: row.name,
          alignment: row.alignment,
          level: row.level,
          data: row.data,
          session: row.session,
        },
      })
      return body.character
    },

    async updateCharacter(id, patch) {
      // Sunucu PATCH'i kısmi kabul eder; yalnızca verilen alanlar gönderilir.
      // `id`/`createdAt` gönderilmez — sunucu bunları yönetir.
      const body: Record<string, unknown> = {}
      if (patch.name !== undefined) body.name = patch.name
      if (patch.alignment !== undefined) body.alignment = patch.alignment
      if (patch.level !== undefined) body.level = patch.level
      if (patch.data !== undefined) body.data = patch.data
      if (patch.session !== undefined) body.session = patch.session

      const result = await request<{ character: CharacterRow }>(
        `/api/characters/${encodeURIComponent(id)}`,
        { method: 'PATCH', json: body },
      )
      return result.character
    },

    async deleteCharacter(id) {
      await request(`/api/characters/${encodeURIComponent(id)}`, { method: 'DELETE' })
    },

    async shareCharacter(id) {
      const body = await request<{ shareToken: string }>(
        `/api/characters/${encodeURIComponent(id)}/share`,
        { method: 'POST' },
      )
      return body.shareToken
    },

    async getSharedCharacter(shareToken) {
      const body = await request<{ character: PartialCharacter | null }>(
        `/api/shared/${encodeURIComponent(shareToken)}`,
      )
      return body.character ? normalizeCharacter(body.character) : null
    },

    // --- İçerik paketleri ------------------------------------------------
    async listPacks() {
      const body = await request<{ packs: ContentPack[] }>('/api/packs')
      return body.packs ?? []
    },

    async savePack(pack, saveOptions) {
      // İstemci doğrulaması KULLANICIYI korur (kendi hatasını erketsin).
      const check = validateContentPack(pack)
      if (!check.ok) {
        throw new WorkerError(`Paket geçersiz: ${check.errors.join(', ')}`, 422)
      }

      const body = await request<{ pack: ContentPack }>(
        `/api/packs/${encodeURIComponent(pack.id)}`,
        {
          method: 'PUT',
          json: {
            name: pack.name,
            version: pack.version,
            description: pack.description,
            isPublic: saveOptions?.isPublic === true,
            pack,
          },
        },
      )
      return body.pack
    },

    async deletePack(packId) {
      await request(`/api/packs/${encodeURIComponent(packId)}`, { method: 'DELETE' })
    },

    async getPublicPack(packId) {
      const body = await request<{ pack: ContentPack | null }>(
        `/api/packs/public/${encodeURIComponent(packId)}`,
      )
      return body.pack ?? null
    },

    // --- Zar arşivi -----------------------------------------------------
    async appendRolls(sessionKey, entries) {
      if (entries.length === 0) return
      await request('/api/rolls', {
        method: 'POST',
        json: {
          sessionKey,
          entries: entries.map((entry) => ({
            formula: entry.formula,
            seed: entry.seed,
            total: entry.total,
            breakdown: entry.breakdown,
          })),
        },
      })
    },

    async listRolls(characterId) {
      const body = await request<{ rolls: RollLogEntry[] }>(
        `/api/characters/${encodeURIComponent(characterId)}/rolls`,
      )
      return body.rolls ?? []
    },

    // --- Davetler --------------------------------------------------------
    async listInvites() {
      const body = await request<{ invites: InviteSummary[] }>('/api/invites')
      return body.invites ?? []
    },

    async createInvite(createOptions) {
      const body = await request<{ invite: InviteSummary }>('/api/invites', {
        method: 'POST',
        json: {
          maxUses: createOptions?.maxUses,
          expiresInDays: createOptions?.expiresInDays,
        },
      })
      return body.invite
    },

    async revokeInvite(code) {
      await request(`/api/invites/${encodeURIComponent(code)}/revoke`, { method: 'POST' })
    },
  }

  return repo
}

interface PublicUser {
  id: string
  email: string
  displayName: string
  role: 'owner' | 'member'
}

/**
 * Paylaşım ucu BİLEREK eksik alan döner: oturum durumu (can, havuzlar) ve
 * sahiplik gönderilmez, çünkü bunlar sahibinin özel verisidir.
 *
 * Bu yüzden burada tam bir `CharacterRow` HALLETİLİR — arayüzün "session
 * undefined" diye korunması gerekmesin. Eksik alanlar güvenli varsayılana
 * çevrilir; uyarı yerine sessizce boş kalır (paylaşımlı görünüm zaten
 * salt-okunurdur).
 */
interface PartialCharacter {
  id: string
  name: string
  alignment: string
  level: number
  data: CharacterRow['data']
  session?: CharacterRow['session']
  shareToken?: string | null
  createdAt?: number
  updatedAt?: number
}

function normalizeCharacter(partial: PartialCharacter): CharacterRow {
  return {
    id: partial.id,
    name: partial.name,
    alignment: partial.alignment ?? '',
    level: partial.level ?? 1,
    data: partial.data,
    session: partial.session ?? {},
    shareToken: partial.shareToken ?? null,
    createdAt: partial.createdAt ?? 0,
    updatedAt: partial.updatedAt ?? 0,
  }
}

/** Worker hatasını kullanıcıya gösterilecek metne çevirir. */
function guard<T>(fn: () => Promise<T>): Promise<T | { ok: false; error: string }> {
  return fn().catch((error: unknown) => {
    if (error instanceof WorkerError) return { ok: false as const, error: error.message }
    return { ok: false as const, error: 'Beklenmeyen bir hata oluştu.' }
  })
}
